import { AppBar, Dialog, DialogActions, Button, DialogContent, Icon, IconButton, Toolbar, Tooltip, Typography, FormControl, TextField, FormControlLabel, Checkbox } from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import { Form } from 'informed';
import moment from 'moment';
import React from 'react';
import { IUserSession } from '../../../@types/employee';
import PumpingStationAuditHelper, { IPumpingStationAudit } from '../../../@types/model/pumpingStation/pumpingStationAudit';
import { IPumpingStationGroup } from '../../../@types/model/pumpingStation/pumpingStationGroup';
import GeneralFunctions from '../../../store/general/functions';
import PumpingStationFlowMeterMakeSelector from '../../customComponents/selector/pumpingStation/PumpingStationFlowMeterMakeSelector';
import PumpingStationFlowMeterPulseSelector from '../../customComponents/selector/pumpingStation/PumpingStationFlowMeterPulseSelector';
import PumpingStationFlowMeterSizeSelector from '../../customComponents/selector/pumpingStation/PumpingStationFlowMeterSizeSelector';

interface IEditPumpingStationFlowmeterProps {
    session : IUserSession;
    pumpingStationAudit : IPumpingStationAudit;
    pumpingGroup : IPumpingStationGroup;
    maxWidth ?: 'xs' | 'sm' | 'md' | 'lg' | false;
    fullScreen ?: boolean;
    fullWidth ?: boolean;
    transition ?: React.ForwardRefExoticComponent<TransitionProps & React.RefAttributes<unknown>>;
    isLoading ?: boolean;
}

interface IEditPumpingStationFlowmeterState {
    open : boolean;
    make : string;
    size : number | null;
    reedSwitch : boolean;
    pulse : string;
    serialNumber : string;
    monitoringSystem : boolean;
    flowmeterImage : string;
}

export default class EditPumpingStationFlowmeter extends React.Component<IEditPumpingStationFlowmeterProps, IEditPumpingStationFlowmeterState> {
    constructor(props : IEditPumpingStationFlowmeterProps) {
        super(props);
        this.state = {
            open: false,
            make: this.props.pumpingGroup.flowMeterMake,
            size: this.props.pumpingGroup.flowMeterSize,
            reedSwitch: this.props.pumpingGroup.flowMeterReedSwitch,
            pulse: this.props.pumpingGroup.flowMeterPulse,
            serialNumber: this.props.pumpingGroup.flowMeterSerial,
            monitoringSystem: this.props.pumpingGroup.monitorSystemConnected,
            flowmeterImage: this.props.pumpingGroup.flowMeterPhotoThumbnailUrl,
        };
    }

    private readonly onMakeChange = (value : string) => {
        this.setState({ make: value });
    };

    private readonly onSizeChange = (value : number | null) => {
        this.setState({ size: value });
    };

    private readonly onPulseChange = (value : string) => {
        this.setState({ pulse: value });
    };

    private readonly onReedSwitchChange = (event : React.ChangeEvent<HTMLInputElement>, checked : boolean) => {
        this.setState({ reedSwitch: checked });
    };

    private readonly onMonitoringSystemChange = (event : React.ChangeEvent<HTMLInputElement>, checked : boolean) => {
        this.setState({ monitoringSystem: checked });
    };

    private readonly onSerialChange = (event : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.setState({ serialNumber: event.target.value });
    };

    private readonly onEditClick = () => {
        this.setState({ open: true });
    };

    private readonly onSubmit = async () => {
        
        const { session } = this.props;

        const { make, size, pulse, serialNumber, reedSwitch, monitoringSystem } = this.state;

        if (!make || !size || !pulse || !serialNumber) {
            GeneralFunctions.generalShowErrorSnackbar('You have left out some information.'); 
            return;
        } 

        const pumpingStationAudit = { ...this.props.pumpingStationAudit, pumpingGroups: [ ...this.props.pumpingStationAudit.pumpingGroups ]};
        
        const pumpingGroupIndex = pumpingStationAudit.pumpingGroups.findIndex((element) => element.reference == this.props.pumpingGroup.reference);

        pumpingStationAudit.updatedBy = session.firebaseUser.uid;
        pumpingStationAudit.updatedByEmployee = session.employee.EmployeeNumber ?? '';
        pumpingStationAudit.updatedByName = session.employee.Name;
        pumpingStationAudit.updatedOn = moment.utc().valueOf();

        pumpingStationAudit.pumpingGroups[pumpingGroupIndex].flowMeterMake = make;
        pumpingStationAudit.pumpingGroups[pumpingGroupIndex].flowMeterSize = size;
        pumpingStationAudit.pumpingGroups[pumpingGroupIndex].flowMeterPulse = pulse;
        pumpingStationAudit.pumpingGroups[pumpingGroupIndex].flowMeterSerial = serialNumber;
        pumpingStationAudit.pumpingGroups[pumpingGroupIndex].flowMeterReedSwitch = reedSwitch;
        pumpingStationAudit.pumpingGroups[pumpingGroupIndex].monitorSystemConnected = monitoringSystem;
        
        try {
            await PumpingStationAuditHelper.save(pumpingStationAudit);

            this.onClose();

            GeneralFunctions.generalShowSuccessSnackbar('Saved changes!');
        } catch {
            GeneralFunctions.generalShowErrorSnackbar('Something went wrong while saving the your changes.');
        }

    };

    private readonly onClose = () => {
        if (this.props.isLoading) return;

        this.setState({ open: false });
    };

    public readonly render = () => {

        const { transition, maxWidth, fullScreen, fullWidth, isLoading, pumpingStationAudit, pumpingGroup } = this.props;
        const { open, make, size, pulse, serialNumber, reedSwitch, monitoringSystem } = this.state;

        return (
            <>
                <Tooltip title={'Edit'}>
                    <IconButton 
                        color={'primary'} 
                        size={'small'} 
                        onClick={this.onEditClick} 
                        aria-label={'Edit'} 
                        disabled={isLoading}
                    >
                        <Icon>edit</Icon>
                    </IconButton> 
                </Tooltip>
                <Dialog
                    open={open}
                    TransitionComponent={transition}
                    transitionDuration={500}
                    maxWidth={maxWidth}
                    fullScreen={fullScreen}
                    fullWidth={fullWidth}
                    aria-labelledby={'pumpingStation-info-info-title'}
                    aria-describedby={'pumpingStation-info-info-description'}
                >
                    <AppBar position='static' >
                        <Toolbar className={'fdr aic jcsb'}>
                            <Typography variant={'h5'} color={'inherit'}>
                                {`Edit ${pumpingStationAudit.pumpCode} - ${moment.utc(pumpingStationAudit.createdOn).format('DD-MM-YYYY')}: PG ${pumpingGroup.number}`}
                            </Typography>
                            <Tooltip title={'Close'}>
                                <IconButton color={'inherit'} disabled={isLoading} onClick={this.onClose}>
                                    <Icon>close</Icon>
                                </IconButton>
                            </Tooltip>
                        </Toolbar>
                    </AppBar>
                    <Form autoComplete={'off'} onSubmit={this.onSubmit} className={'fdc flx1 hfill oya'}>
                        <DialogContent className={'fdc hfill'}>
                            <div className={'fdr flx1'}>
                                <PumpingStationFlowMeterMakeSelector 
                                    value={make}
                                    disabled={isLoading}
                                    required={true}
                                    onChange={this.onMakeChange}
                                />
                                <PumpingStationFlowMeterSizeSelector 
                                    value={size}
                                    disabled={isLoading}
                                    required={true}
                                    className={'ml20'}
                                    onChange={this.onSizeChange}
                                />
                            </div>
                            <div className='fdr flx1 aic'>
                                <PumpingStationFlowMeterPulseSelector 
                                    value={pulse}
                                    disabled={isLoading}
                                    required={true}
                                    onChange={this.onPulseChange}
                                />
                                <FormControl margin={'normal'} className={'ml20'} fullWidth={true}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox 
                                                color={'primary'} 
                                                checked={reedSwitch} 
                                                onChange={this.onReedSwitchChange}  
                                            />
                                        }
                                        label={'Reed Switch?'}
                                    />
                                </FormControl>
                            </div>
                            <div className='fdr aic flx1'>
                                <FormControl fullWidth={true} >
                                    <TextField 
                                        type={'text'}
                                        autoComplete={'off'}
                                        id={'serialNumber'}
                                        label={'Serial Number'}
                                        value={serialNumber}
                                        margin={'normal'}
                                        className={'TextField'}
                                        error={!serialNumber}
                                        onChange={this.onSerialChange}
                                    />
                                </FormControl>
                                <FormControl margin={'normal'} fullWidth={true} className={'ml20'}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox 
                                                color={'primary'} 
                                                checked={monitoringSystem} 
                                                onChange={this.onMonitoringSystemChange}  
                                            />
                                        }
                                        label={'Connected Monitoring System?'}
                                    />
                                </FormControl>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button type={'submit'} disabled={isLoading} variant='contained' color='primary'>
                                OK
                            </Button>
                            <Button disabled={isLoading} variant='outlined' onClick={this.onClose} color='primary' autoFocus>
                                CANCEL
                            </Button>
                        </DialogActions>
                    </Form>
                </Dialog>
            </>
        );
    };
}